import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  MdArrowBack, MdArrowForward, MdCheckCircle,
  MdEmail, MdAccountBalance, MdVerified, MdInfo,
  MdLocalHospital, MdWarning, MdCheck, MdPause
} from 'react-icons/md';
import { hospitalAPI, setHospitalAppSession, loadHospitalAppSession, clearHospitalAppSession, getStoredApplicationId } from '../services/api';
import LocationMapPicker from '../components/LocationMapPicker';
import LocationCascadeSelect from '../components/LocationCascadeSelect';
import { STATES } from '../data/indiaLocations';
import { resolveFromGeocode } from '../utils/resolveGeocodeLocation';

const STEPS = [
  { id: 0, title: 'Verify Email', icon: <MdEmail /> },
  { id: 1, title: 'Basic Info', icon: <MdLocalHospital /> },
  { id: 2, title: 'Legal & KYC', icon: <MdVerified /> },
  { id: 3, title: 'Bank Details', icon: <MdAccountBalance /> },
  { id: 4, title: 'Review & Submit', icon: <MdCheckCircle /> },
];

const HOSPITAL_TYPES = ['Private', 'Government', 'Trust', 'Corporate', 'Clinic', 'Diagnostic Center'];

// ── GST state-code → state name map (1st two digits of GSTIN) ──────────────
// Used to auto-fill the State field the moment a valid GSTIN is typed.
const GST_STATE_CODES = {
  '01':'Jammu and Kashmir','02':'Himachal Pradesh','03':'Punjab','04':'Chandigarh',
  '05':'Uttarakhand','06':'Haryana','07':'Delhi','08':'Rajasthan','09':'Uttar Pradesh',
  '10':'Bihar','11':'Sikkim','12':'Arunachal Pradesh','13':'Nagaland','14':'Manipur',
  '15':'Mizoram','16':'Tripura','17':'Meghalaya','18':'Assam','19':'West Bengal',
  '20':'Jharkhand','21':'Odisha','22':'Chhattisgarh','23':'Madhya Pradesh','24':'Gujarat',
  '25':'Daman and Diu','26':'Dadra and Nagar Haveli','27':'Maharashtra','28':'Andhra Pradesh (Old)',
  '29':'Karnataka','30':'Goa','31':'Lakshadweep','32':'Kerala','33':'Tamil Nadu',
  '34':'Puducherry','35':'Andaman and Nicobar Islands','36':'Telangana','37':'Andhra Pradesh',
  '38':'Ladakh','97':'Other Territory',
};

// GST regex: 2 digits state + 5 letters + 4 digits + 1 letter + 1 alphanum + Z + 1 alphanum
const GST_RX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
const PAN_RX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const CIN_RX = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
const IFSC_RX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

// HCEN_Website_API Lambda outbound IP (whitelisted in CGPE) — never use checkip / other IPs in UI.
const HCEN_WHITELIST_IP = '13.239.102.137';
const HCEN_WHITELIST_HINT = `Add this IP in merchant API Keys whitelist: ${HCEN_WHITELIST_IP}`;

function normalizeKycHealth(data) {
  if (!data || typeof data !== 'object') return data;
  return {
    ...data,
    outboundIp: HCEN_WHITELIST_IP,
    whitelistHint: HCEN_WHITELIST_HINT,
  };
}

// Robustly read a query param from the FULL current URL.
// code/state/codeVerifier to our redirectUrl which already has query params; some
// providers append with "?" instead of "&", producing a malformed URL where
// URLSearchParams.get() returns null. This regex matches the param after either
// "?" or "&" (or even a stray second "?"), so the OAuth return always works.
function readUrlParam(name) {
  try {
    const m = window.location.href.match(new RegExp('[?&]' + name + '=([^&#?]*)', 'i'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) {
    return null;
  }
}

const BED_TYPE_KEYS = ['general', 'semiPrivate', 'private', 'deluxe', 'nicu', 'hdu', 'otCount'];

// type="number" lets the mouse wheel and browser spinners change values while
// typing (e.g. 300 → 299). Plain text + digits-only keeps what the user typed.
function digitsOnly(raw, maxLen) {
  const s = String(raw ?? '').replace(/\D/g, '');
  return maxLen != null ? s.slice(0, maxLen) : s;
}

function formDigits(value) {
  if (value == null || value === '') return '';
  return String(value);
}

function parseStoredInt(value) {
  const s = String(value ?? '').trim();
  if (!s) return 0;
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function emptyBedTypesPayload() {
  return Object.fromEntries(BED_TYPE_KEYS.map((k) => [k, 0]));
}

function noBedsFormFields() {
  return { totalBeds: 0, icuBeds: 0, bedTypes: emptyBedTypesPayload() };
}

function normalizeBedTypesForForm(raw) {
  const empty = Object.fromEntries(BED_TYPE_KEYS.map((k) => [k, '']));
  if (!raw || typeof raw !== 'object') return empty;
  return {
    ...empty,
    ...Object.fromEntries(
      Object.entries(raw).map(([k, v]) => [k, formDigits(v)])
    ),
  };
}

function isRegistryUnavailableError(msg) {
  const m = String(msg || '').toLowerCase();
  return /ip not allowed|ip not whitelisted|ip address not|whitelist.*ip|ip.*whitelist|forbidden ip|your ip|source ip/.test(m);
}

function isTransientKycFailure(result, networkErr) {
  if (networkErr) {
    const m = String(networkErr).toLowerCase();
    return /network|timeout|failed to fetch|502|503|504|429|aborted|econnreset|load failed/.test(m);
  }
  if (!result) return true;
  if (result.status === 'pending') return false;
  const http = result.providerHttpStatus || 0;
  if (http >= 502 && http <= 504) return true;
  const note = String(result.lookupNote || result.error || result.providerMessage || '').toLowerCase();
  return /timeout|temporarily unavailable|service unavailable|network|502|503|504|429|rate limit|econnreset|cold start|try again/.test(note);
}

function kycResultHasData(kind, result) {
  if (!result || typeof result !== 'object') return false;
  if (result.verified === true || result.status === 'partial') return true;
  return Boolean(kycPrimaryName(kind, result.details));
}

function parseKycApiResponse(r, fieldKey, fieldValue) {
  if (r?.data && typeof r.data === 'object') {
    return normalizeKycApiResult(r.data, fieldKey, fieldValue);
  }
  if (r?.error) {
    return {
      verified: false,
      status: 'api_error',
      error: r.error,
      lookupNote: r.error,
      [fieldKey]: fieldValue,
    };
  }
  return null;
}

const KYC_AUTO_RETRIES = 2;
const KYC_VERIFY_DEBOUNCE_MS = 700;

function sanitizeKycError(msg) {
  if (isRegistryUnavailableError(msg)) return null;
  const m = String(msg || '');
  return m
    .replace(/cashfree|cgpey?/gi, 'registry')
    .replace(/verification[\s-]*service/gi, 'registry')
    .replace(/https?:\/\/\S+/g, '')
    .trim();
}

function normalizeKycApiResult(payload, fieldKey, fieldValue) {
  if (!payload || typeof payload !== 'object') return payload;
  if (fieldKey && fieldValue && !payload[fieldKey]) {
    return { ...payload, [fieldKey]: fieldValue };
  }
  return payload;
}

// ── Razorpay free public IFSC API ──────────────────────────────────────────
// Endpoint: GET https://ifsc.razorpay.com/<IFSC>
// Returns { BANK, IFSC, BRANCH, ADDRESS, CITY, DISTRICT, STATE, CONTACT, ... }
// No auth, no rate limit issues in normal use. Used to auto-fill bankName +
// branchName instantly when the user types a valid IFSC.
async function lookupIfsc(ifsc) {
  if (!IFSC_RX.test(ifsc)) return null;
  try {
    const r = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

// Subtle registry hint under each field — shows names + details from government records.
function kycPrimaryName(kind, details) {
  const d = details || {};
  if (kind === 'gst') return d.legalName || d.businessName || '';
  if (kind === 'pan') return d.registeredName || '';
  if (kind === 'cin') return d.companyName || '';
  if (kind === 'bank') return d.nameAtBank || '';
  return '';
}

function kycDetailRows(kind, result) {
  const d = result?.details || {};
  const rows = [];
  const push = (label, value) => {
    if (value != null && String(value).trim() !== '') rows.push({ label, value: String(value).trim() });
  };

  if (kind === 'gst') {
    push('Legal name', d.legalName);
    if (d.businessName && d.businessName !== d.legalName) push('Trade name', d.businessName);
    push('GST status', d.gstStatus);
    push('Taxpayer type', d.taxpayerType);
    push('Constitution', d.constitution);
    push('Registered address', d.address);
  } else if (kind === 'pan') {
    push('Name on PAN', d.registeredName);
    push('PAN status', d.panStatus);
    push('Category', d.panType);
    if (d.dob) push('Date of birth', d.dob);
    if (d.nameMatchScore != null) push('Name match', `${d.nameMatchScore}%`);
  } else if (kind === 'cin') {
    push('Company name', d.companyName);
    push('MCA status', d.companyStatus);
    if (d.mcaStatus && d.mcaStatus !== d.companyStatus) push('Lookup status', d.mcaStatus);
    push('Company type', d.companyType);
    push('ROC', d.rocCode);
    push('Registration no.', d.registrationNumber);
    push('Incorporated', d.incorporationDate);
    push('Registered address', d.registeredAddress);
    if (Array.isArray(d.directors) && d.directors.length) {
      push('Directors', d.directors.join(', '));
    }
  } else if (kind === 'bank') {
    push('Account holder', d.nameAtBank);
    push('Verification', d.verificationMethod === 'penny_drop' ? 'Bank penny drop (NPCI)' : null);
    push('Account status', d.accountStatus);
    if (d.nameMatchScore != null) push('Name match', `${d.nameMatchScore}%`);
  }
  return rows;
}

function KycRegistryPanel({ busy, result, onRetry, kind }) {
  const title =
    kind === 'gst' ? 'GST records'
    : kind === 'pan' ? 'PAN records'
    : kind === 'cin' ? 'MCA company records'
    : 'Bank account records';

  const busyLabel =
    kind === 'bank'
      ? 'Verifying bank account — can take up to 30 seconds, please wait…'
      : kind === 'cin'
        ? (result?.lookupNote || 'MCA lookup in progress — can take up to 60 seconds, please wait…')
        : `Checking ${title.toLowerCase()} — this can take up to 25 seconds, please wait…`;

  if (busy || result?.status === 'pending') {
    return (
      <div className="kyc-verify-card kyc-verify-card--busy">
        <span className="anim-spin kyc-verify-card__spin" />
        <span>{busy ? busyLabel : (result?.lookupNote || busyLabel)}</span>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="kyc-verify-card kyc-verify-card--hint">
        {kind === 'gst' || kind === 'cin'
          ? 'Optional — details appear automatically when a valid number is entered'
          : 'Details appear automatically when you finish typing'}
      </div>
    );
  }

  const name = kycPrimaryName(kind, result.details);
  const rows = kycDetailRows(kind, result);
  const confirmed = result.verified === true;
  const partial = !confirmed && (result.status === 'partial' || Boolean(name));
  const formatOnly = result.status === 'format_verified' || result.status === 'skipped';

  if (formatOnly && !name && !rows.length) {
    const mode = result.lookupMode || 'not_configured';
    const note = result.lookupNote || '';
    if (mode === 'not_configured') {
      return (
        <div className="kyc-verify-card kyc-verify-card--warn">
          <strong>Format OK</strong> — live name lookup is <strong>not connected</strong> on the server yet.
          Government names will appear here automatically once verification keys are set on AWS Lambda.
        </div>
      );
    }
    if (mode === 'unavailable') {
      return (
        <div className="kyc-verify-card kyc-verify-card--warn">
          {note || 'Government lookup is temporarily unavailable.'}
          {onRetry && (
            <button type="button" className="kyc-verify-card__retry" onClick={onRetry}>Retry lookup</button>
          )}
        </div>
      );
    }
    return (
      <div className="kyc-verify-card kyc-verify-card--pending">
        Format validated — waiting for live record lookup.
      </div>
    );
  }

  if (result.status === 'pending') {
    return null;
  }

  if (result.status === 'api_error' || (!confirmed && !partial && result.error)) {
    const msg = result.lookupNote || sanitizeKycError(result.error);
    if (msg) {
      return (
        <div className="kyc-verify-card kyc-verify-card--warn">
          <div>{msg}</div>
          {result.whitelistHint && (
            <div style={{ marginTop:6, fontSize:11, color:'#fbbf24' }}>
              {result.whitelistHint}
            </div>
          )}
          {result.providerHttpStatus === 403 && !result.whitelistHint && (
            <div style={{ marginTop:6, fontSize:11, color:'var(--text-muted)' }}>
              In your verification merchant dashboard → API Keys → add the AWS Lambda outbound IP (not your home PC IP).
            </div>
          )}
          {kind === 'cin' && (
            <div style={{ marginTop:6, fontSize:11, color:'var(--text-muted)' }}>
              CIN is optional — you can continue registration; HCEN verifies company details during approval.
            </div>
          )}
          {onRetry && (
            <button type="button" className="kyc-verify-card__retry" onClick={onRetry}>Retry lookup</button>
          )}
        </div>
      );
    }
  }

  if (!name && !rows.length) return null;

  return (
    <div className={`kyc-verify-card ${confirmed ? 'kyc-verify-card--ok' : 'kyc-verify-card--partial'}`}>
      <div className="kyc-verify-card__head">
        <span className="kyc-verify-card__dot" />
        <div>
          <div className="kyc-verify-card__title">
            {confirmed
              ? (kind === 'bank' ? 'Confirmed with bank records' : 'Confirmed with government records')
              : 'Record found — pending final confirmation'}
          </div>
          {name && <div className="kyc-verify-card__name">{name}</div>}
        </div>
      </div>
      {rows.length > 0 && (
        <dl className="kyc-verify-card__grid">
          {rows.map((row) => (
            <div key={row.label} className="kyc-verify-card__row">
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {!confirmed && onRetry && (
        <button type="button" className="kyc-verify-card__retry" onClick={onRetry}>Refresh lookup</button>
      )}
    </div>
  );
}

function KycSummaryPanel({ kyc, data }) {
  const blocks = [
    { key: 'gst', label: 'GST', value: data.gstNumber, result: kyc.gst },
    { key: 'pan', label: 'PAN', value: data.panNumber, result: kyc.pan },
    { key: 'cin', label: 'CIN', value: data.cinNumber, result: kyc.cin },
    { key: 'bank', label: 'Bank', value: data.accountNumber ? `****${data.accountNumber.slice(-4)}` : '', result: kyc.bank },
  ].filter((b) => b.value);

  if (!blocks.length) return null;

  return (
    <div className="kyc-summary-panel">
      <h4 className="kyc-summary-panel__title">Verified business details</h4>
      <p className="kyc-summary-panel__sub">
        Pulled automatically from government and banking records to speed up your approval.
      </p>
      <div className="kyc-summary-panel__list">
        {blocks.map((b) => {
          const name = kycPrimaryName(b.key, b.result?.details);
          const rows = kycDetailRows(b.key, b.result);
          if (!name && !rows.length) {
            return (
              <div key={b.key} className="kyc-summary-panel__item kyc-summary-panel__item--muted">
                <strong>{b.label}</strong> · {b.value}
                <span>
                  {b.result?.lookupMode === 'not_configured'
                    ? 'Live lookup not connected on server'
                    : b.result?.lookupMode === 'unavailable'
                      ? (b.result?.lookupNote || 'Lookup temporarily unavailable')
                      : 'Waiting for live lookup'}
                </span>
              </div>
            );
          }
          return (
            <div key={b.key} className="kyc-summary-panel__item">
              <div className="kyc-summary-panel__item-head">
                <strong>{b.label}</strong> · {b.value}
                {b.result?.verified && <span className="kyc-summary-panel__badge">Confirmed</span>}
              </div>
              {name && <div className="kyc-summary-panel__name">{name}</div>}
              {rows.slice(0, 4).map((row) => (
                <div key={row.label} className="kyc-summary-panel__meta">{row.label}: {row.value}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HospitalRegistration() {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlResumeParam = searchParams.get('resume') || readUrlParam('resume') || '';
  const initialResumeInput = /^APP-/i.test(urlResumeParam) ? urlResumeParam.trim().toUpperCase() : '';
  
  // Verification states
  const [verificationStep, setVerificationStep] = useState('email'); // 'email', 'otp', 'verified'
  const [verifying, setVerifying] = useState(false);
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [applicationId, setApplicationId] = useState(null);
  
  // Resume-with-OTP (security: never auto-load from localStorage or URL alone)
  const [resumeAppIdInput, setResumeAppIdInput] = useState(initialResumeInput);
  const [resumeStep, setResumeStep] = useState('idle'); // idle | otp
  const [pendingResumeId, setPendingResumeId] = useState('');
  const [resumeEmailMasked, setResumeEmailMasked] = useState('');
  const [resumeOtp, setResumeOtp] = useState('');
  const [resumeResendTimer, setResumeResendTimer] = useState(0);
  const [resumePanelFocus, setResumePanelFocus] = useState(false);
  
  // Form flow states
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [loading, setLoading] = useState(false);
  // Admin-flagged fixes returned when this application is being resumed in
  // "needs_resubmission" state. Each entry: { section, field, label, issue }.
  const [requestedFixes, setRequestedFixes] = useState([]);
  const [appStatus, setAppStatus] = useState('');

  // ── KYC verification state (CGPE registry · Jun 2026) ───────────────────
  // Shape per key: { verifying: bool, result: { verified, status, error?, details } | null }
  // Keys: 'gst', 'pan', 'bank'.
  const [kycLiveLookup, setKycLiveLookup] = useState(null); // { liveLookup, reason } | null
  const [kyc, setKyc] = useState({ gst: null, pan: null, bank: null, cin: null, okyc: null });
  const [kycBusy, setKycBusy] = useState({ gst: false, pan: false, bank: false, cin: false });
  // ── IFSC lookup state (May 18, 2026) — used to show inline "Detected: HDFC,
  // MG Road branch" hint while typing IFSC, before bank verify even runs.
  const [ifscLookup, setIfscLookup] = useState(null);   // { BANK, BRANCH, CITY, STATE } | null
  const [ifscBusy, setIfscBusy] = useState(false);
  // Debounce timers for auto-verify (one per kind) so we don't fire on every
  // keystroke. Cleared & restarted on each change.
  const autoVerifyTimers = useRef({ gst: null, pan: null, bank: null, ifsc: null, cin: null });
  const kycVerifyQueue = useRef(Promise.resolve());
  const gstVerifyGen = useRef(0);
  const panVerifyGen = useRef(0);
  const bankVerifyGen = useRef(0);
  const cinVerifyGen = useRef(0);
  const kycStepRefreshDone = useRef({ step2: false, step3: false });
  const cinPollRef = useRef(null);

  // Form data
  const [data, setData] = useState({
    // Verification
    email: '',
    contactName: '',
    
    // Step 1: Basic
    hospitalName: '',
    phone: '',
    emergencyPhone: '',
    hospitalType: 'Private',
    address: '',
    city: '',
    mandal: '',
    district: '',
    state: '',
    cityCode: '',
    locality: '',
    pincode: '',
    website: '',
    latitude: null,
    longitude: null,

    // Authorized person / hospital owner — legally accountable signatory.
    // Captured separately from the day-to-day contactName so HCEN admin can
    // verify against PAN / registration certificate.
    ownerName: '',
    ownerDesignation: 'Owner',
    ownerPhone: '',
    
    // Step 2: Legal/KYC
    registrationNumber: '',
    registrationDate: '',
    gstNumber: '',
    panNumber: '',
    cinNumber: '',
    totalBeds: '',
    icuBeds: '',
    
    // Step 3: Bank
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    branchName: '',
    upiId: '',
    
    // Step 4: Files
    documents: {},

    // Hospital Details (May 25, 2026 · Phase 2 Slice 4 — patient-trust fields)
    establishmentYear: '',
    about:             '',
    // OP timings (May 25, 2026 · admin Basic-Info step requires these)
    opStart:           '09:00',
    opEnd:             '21:00',
    opFee:             '',
    is24x7:            false,
    hasEmergency:      false,
    languagesSpoken:   [],
    amenities: {
      pharmacy:    false,
      bloodBank:   false,
      lab24x7:     false,
      ambulance:   false,
      parking:     false,
      wheelchair:  false,
      cafeteria:   false,
      teleConsult: false,
      homeVisit:   false,
    },
    bedTypes: {
      general:      '',
      semiPrivate:  '',
      private:      '',
      deluxe:       '',
      nicu:         '',
      hdu:          '',
      otCount:      '',
    },
  });

  // Restore session token from localStorage on every visit
  useEffect(() => {
    loadHospitalAppSession();
  }, []);

  function hydrateFormDataFromServer(fd) {
    if (!fd || typeof fd !== 'object') return {};
    const bank = fd.bankDetails || {};
    return {
      ...fd,
      accountHolderName: fd.accountHolderName || bank.accountHolderName || '',
      accountNumber: fd.accountNumber || bank.accountNumber || '',
      ifscCode: fd.ifscCode || bank.ifscCode || '',
      bankName: fd.bankName || bank.bankName || '',
      branchName: fd.branchName || bank.branchName || '',
      upiId: fd.upiId || bank.upiId || '',
      languagesSpoken: Array.isArray(fd.languagesSpoken) ? fd.languagesSpoken : [],
      opFee: formDigits(fd.opFee),
      totalBeds: formDigits(fd.totalBeds),
      icuBeds: formDigits(fd.icuBeds),
      establishmentYear: formDigits(fd.establishmentYear),
      bedTypes: normalizeBedTypesForForm(fd.bedTypes),
      amenities: fd.amenities && typeof fd.amenities === 'object' ? fd.amenities : {},
    };
  }

  function buildDraftFormData() {
    return {
      hospitalName: data.hospitalName,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone,
      emergencyPhone: data.emergencyPhone,
      hospitalType: data.hospitalType,
      address: data.address,
      city: data.city,
      mandal: data.mandal || data.city,
      district: data.district,
      state: data.state,
      cityCode: data.cityCode,
      locality: data.locality,
      pincode: data.pincode,
      website: data.website,
      latitude: data.latitude,
      longitude: data.longitude,
      ownerName: data.ownerName,
      ownerDesignation: data.ownerDesignation,
      ownerPhone: data.ownerPhone,
      registrationNumber: data.registrationNumber,
      registrationDate: data.registrationDate,
      gstNumber: data.gstNumber,
      panNumber: data.panNumber,
      cinNumber: data.cinNumber,
      ...noBedsFormFields(),
      establishmentYear: data.establishmentYear ? parseInt(data.establishmentYear, 10) || null : null,
      about: data.about || '',
      opStart: data.is24x7 ? '00:00' : (data.opStart || ''),
      opEnd: data.is24x7 ? '23:59' : (data.opEnd || ''),
      opFee: parseStoredInt(data.opFee),
      is24x7: !!data.is24x7,
      hasEmergency: !!data.hasEmergency,
      languagesSpoken: Array.isArray(data.languagesSpoken) ? data.languagesSpoken : [],
      amenities: data.amenities || {},
      bankDetails: {
        accountHolderName: data.accountHolderName,
        accountNumber: data.accountNumber,
        ifscCode: data.ifscCode,
        bankName: data.bankName,
        branchName: data.branchName,
        upiId: data.upiId,
      },
    };
  }

  const applyApplicationPayload = (id, r) => {
    setApplicationId(id);
    const sess = loadHospitalAppSession();
    setHospitalAppSession(sess.token, r.email || sess.email || data.email, id);
    const hydratedDocs = {};
    const serverDocUrls = r.documentUrls || (r.formData && r.formData.documentUrls) || {};
    Object.entries(serverDocUrls).forEach(([k, url]) => {
      if (typeof url === 'string' && url) {
        const fileName = url.split('/').pop() || k;
        hydratedDocs[k] = { url, name: fileName, size: 0, uploading: false };
      }
    });
    setData(prev => ({
      ...prev,
      email: r.email || '',
      contactName: r.contactName || '',
      hospitalName: r.hospitalName || '',
      ...hydrateFormDataFromServer(r.formData),
      documents: hydratedDocs,
    }));
    if (r.kyc && typeof r.kyc === 'object') {
      setKyc({
        gst:  r.kyc.gst  || null,
        pan:  r.kyc.pan  || null,
        bank: r.kyc.bank || null,
        cin:  r.kyc.cin  || null,
        okyc: r.kyc.okyc || null,
      });
    }
    const resumedStep = Math.min(Math.max(0, Number(r.currentStep) || 1), STEPS.length - 1);
    setStep(resumedStep);
    setVerificationStep('verified');
    setAppStatus(r.status || '');
    kycStepRefreshDone.current = { step2: false, step3: false };
    let fixes = r.requestedFixes;
    if (typeof fixes === 'string') { try { fixes = JSON.parse(fixes); } catch { fixes = []; } }
    setRequestedFixes(Array.isArray(fixes) ? fixes : []);
    if (r.status === 'needs_resubmission') {
      setSaveMessage('Reviewer has requested changes — please address the items below and resubmit.');
    } else {
      setSaveMessage(`Resumed at Step ${resumedStep + 1} of ${STEPS.length}`);
    }
    setResumeStep('idle');
    setPendingResumeId('');
    setResumeOtp('');
    setResumePanelFocus(false);
  };

  // Secure entry: never auto-resume from localStorage. OTP required when resuming from login.
  // Same-tab refresh may restore from sessionStorage only (no cross-hospital bleed).
  useEffect(() => {
    let cancelled = false;
    const fresh = searchParams.get('fresh');
    const resumeMode = searchParams.get('resume') || readUrlParam('resume') || '';
    const esignReturn = searchParams.get('esign') || readUrlParam('esign') || '';

    const resetToEntry = (message) => {
      setApplicationId(null);
      setStep(0);
      setVerificationStep('email');
      setResumeStep('idle');
      setPendingResumeId('');
      setResumeOtp('');
      if (message) setSaveMessage(message);
    };

    if (fresh === '1' || fresh === 'true') {
      clearHospitalAppSession();
      resetToEntry('Start a new hospital application — verify your email below.');
      return;
    }

    if (resumeMode === 'prompt' || resumeMode === 'true') {
      clearHospitalAppSession();
      resetToEntry('');
      setResumeAppIdInput('');
      setResumePanelFocus(true);
      setSaveMessage('Enter your Application ID — we will send an email OTP to verify it is you.');
      return;
    }

    const storedAppId = (getStoredApplicationId() || '').trim().toUpperCase();
    const resumeFromUrl = (readUrlParam('resume') || urlResumeParam || '').trim().toUpperCase();
    const oauthTargetId = /^APP-/i.test(resumeFromUrl) ? resumeFromUrl : storedAppId;
    const oauthReturn = esignReturn === 'return' && /^APP-/i.test(oauthTargetId);

    if (oauthReturn) {
      (async () => {
        loadHospitalAppSession();
        const storedId = getStoredApplicationId();
        const sess = loadHospitalAppSession();
        const targetId = oauthTargetId;
        if (storedId === targetId && sess.token) {
          setLoading(true);
          const r = await hospitalAPI.getApplication(targetId);
          if (!cancelled && !r.error && r.data) {
            applyApplicationPayload(targetId, r.data);
          } else if (!cancelled) {
            clearHospitalAppSession();
            resetToEntry('');
            setResumeAppIdInput(targetId);
            setResumePanelFocus(true);
            setError('Session expired — verify with email OTP to continue.');
          }
          if (!cancelled) setLoading(false);
        } else if (!cancelled) {
          resetToEntry('');
          setResumeAppIdInput(targetId);
          setResumePanelFocus(true);
          setSaveMessage('Verify with email OTP to continue your application.');
        }
      })();
      return () => { cancelled = true; };
    }

    if (/^APP-/i.test(urlResumeParam)) {
      resetToEntry('');
      setResumeAppIdInput(urlResumeParam.trim().toUpperCase());
      setResumePanelFocus(true);
      setSaveMessage('Enter the email OTP for this Application ID to continue.');
      return;
    }

    // Same-tab page refresh mid-application (sessionStorage only — not localStorage)
    loadHospitalAppSession();
    const storedId = getStoredApplicationId();
    const sess = loadHospitalAppSession();
    if (!storedId || !sess.token || !/^APP-/i.test(storedId)) {
      try {
        if (localStorage.getItem('hcenAppId')) clearHospitalAppSession();
      } catch (_) {}
      return;
    }

    (async () => {
      setLoading(true);
      const r = await hospitalAPI.getApplication(storedId);
      if (!cancelled && !r.error && r.data) {
        applyApplicationPayload(storedId, r.data);
      } else if (!cancelled) {
        clearHospitalAppSession();
        resetToEntry('');
        setResumeAppIdInput(storedId);
        setResumePanelFocus(true);
        setError('Session expired — use Resume below with your Application ID and email OTP.');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchParams.get('fresh'),
    searchParams.get('resume'),
    urlResumeParam,
    searchParams.get('esign'),
  ]);
  
  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(r => r - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (resumeResendTimer > 0) {
      const timer = setTimeout(() => setResumeResendTimer(t => t - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resumeResendTimer]);

  useEffect(() => {
    if (step !== 2 && step !== 3) return;
    let cancelled = false;
    (async () => {
      const { data } = await hospitalAPI.kycHealth();
      if (!cancelled && data) setKycLiveLookup(normalizeKycHealth(data));
    })();
    return () => { cancelled = true; };
  }, [step]);
  
  const requestResumeOtp = async () => {
    const v = resumeAppIdInput.trim().toUpperCase();
    if (!/^APP-/.test(v)) {
      setError('Enter a valid Application ID (starts with "APP-").');
      return;
    }
    setError('');
    setSaveMessage('');
    setVerifying(true);
    try {
      const r = await hospitalAPI.requestResumeOtp(v);
      if (r.error) {
        setError(r.error);
        return;
      }
      setPendingResumeId(v);
      setResumeEmailMasked(r.data?.emailMasked || 'your registered email');
      setResumeStep('otp');
      setResumeOtp('');
      setResumeResendTimer(60);
      setSaveMessage(`Verification code sent to ${r.data?.emailMasked || 'your registered email'}`);
    } catch (_) {
      setError('Could not send verification code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const verifyResumeOtp = async () => {
    if (!pendingResumeId) {
      setError('Request a verification code first');
      return;
    }
    if (resumeOtp.length !== 6) {
      setError('Enter 6-digit OTP');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const r = await hospitalAPI.verifyResumeOtp(pendingResumeId, resumeOtp);
      if (r.error) {
        setError(r.error);
        return;
      }
      const payload = r.data || {};
      const sess = payload.sessionToken;
      if (sess) {
        setHospitalAppSession(sess, payload.email, pendingResumeId);
      }
      applyApplicationPayload(pendingResumeId, payload);
      setSaveMessage(`Application ${pendingResumeId} verified — welcome back!`);
    } catch (_) {
      setError('Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const cancelResume = () => {
    setResumeStep('idle');
    setPendingResumeId('');
    setResumeOtp('');
    setResumeEmailMasked('');
    setResumePanelFocus(false);
    setError('');
    setSaveMessage('');
  };

  const updateField = (section, field, value) => {
    setData(p => ({ ...p, [field]: value }));
  };

  // ─── KYC VERIFICATION (CGPE via Website Lambda) ───────────────────────────
  // Each verify call hits the Website Lambda which calls CGPE synchronously
  // and persists the result onto HospitalApplications.kyc.{gst|pan|bank}.
  // The badge next to each field reads from `kyc[key]` and shows
  // "Verifying…" / "✓ Verified — <legalName>" / "✗ <error>".

  const runKycQueued = (task) => {
    const next = kycVerifyQueue.current
      .then(() => new Promise((resolve) => { setTimeout(resolve, 80); }))
      .then(() => task())
      .catch(() => {});
    kycVerifyQueue.current = next;
    return next;
  };

  const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

  const runKycVerifyLoop = async ({
    kind,
    gen,
    genRef,
    busyKey,
    fieldKey,
    fieldValue,
    apiCall,
    onSuccess,
  }) => {
    setKyc(k => ({ ...k, [kind]: null }));
    setKycBusy(b => ({ ...b, [busyKey]: true }));
    try {
      for (let attempt = 0; attempt <= KYC_AUTO_RETRIES; attempt += 1) {
        if (gen !== genRef.current) return;
        if (attempt > 0) await sleep(700 * attempt);
        const r = await apiCall();
        if (gen !== genRef.current) return;
        const parsed = parseKycApiResponse(r, fieldKey, fieldValue);
        if (parsed && kycResultHasData(kind, parsed)) {
          setKyc(k => ({ ...k, [kind]: parsed }));
          if (onSuccess) onSuccess(parsed);
          return;
        }
        if (parsed?.status === 'pending') {
          setKyc(k => ({ ...k, [kind]: parsed }));
          return parsed;
        }
        const canRetry = attempt < KYC_AUTO_RETRIES && isTransientKycFailure(parsed, r?.error);
        if (!canRetry) {
          if (parsed) setKyc(k => ({ ...k, [kind]: parsed }));
          return parsed;
        }
      }
    } finally {
      if (gen === genRef.current) setKycBusy(b => ({ ...b, [busyKey]: false }));
    }
    return null;
  };

  const verifyGst = async () => {
    const g = (data.gstNumber || '').toUpperCase();
    if (g.length !== 15 || !GST_RX.test(g)) return;
    const gen = ++gstVerifyGen.current;
    return runKycQueued(async () => {
      await runKycVerifyLoop({
        kind: 'gst',
        gen,
        genRef: gstVerifyGen,
        busyKey: 'gst',
        fieldKey: 'gstNumber',
        fieldValue: g,
        apiCall: () => hospitalAPI.verifyGST(g, applicationId),
        onSuccess: (gstResult) => {
          const gstName = gstResult?.details?.legalName || gstResult?.details?.businessName;
          if (gstName && !data.hospitalName.trim()) {
            setData(p => ({
              ...p,
              hospitalName: p.hospitalName?.trim() ? p.hospitalName : gstName,
            }));
          }
        },
      });
    });
  };

  const verifyPan = async () => {
    const p = (data.panNumber || '').toUpperCase();
    if (p.length !== 10 || !PAN_RX.test(p)) return;
    const gen = ++panVerifyGen.current;
    return runKycQueued(async () => {
      await runKycVerifyLoop({
        kind: 'pan',
        gen,
        genRef: panVerifyGen,
        busyKey: 'pan',
        fieldKey: 'panNumber',
        fieldValue: p,
        apiCall: () => hospitalAPI.verifyPAN(p, data.ownerName || data.contactName || null, applicationId),
        onSuccess: (panResult) => {
          const panName = panResult?.details?.registeredName;
          if (panName && !data.ownerName.trim()) {
            setData(prev => ({ ...prev, ownerName: panName }));
          }
        },
      });
    });
  };

  const pollCinUntilDone = async (expectedCin) => {
    if (!applicationId) {
      setKycBusy(b => ({ ...b, cin: false }));
      return;
    }
    cinPollRef.current = expectedCin;
    for (let i = 0; i < 25; i += 1) {
      if (cinPollRef.current !== expectedCin) return;
      await new Promise(r => setTimeout(r, 3000));
      const app = await hospitalAPI.getApplication(applicationId);
      const cin = app.data?.kyc?.cin;
      if (!cin || cin.cinNumber !== expectedCin || cin.status === 'pending') continue;
      if (cinPollRef.current !== expectedCin) return;
      cinPollRef.current = null;
      setKycBusy(b => ({ ...b, cin: false }));
      const cinResult = normalizeKycApiResult(cin, 'cinNumber', expectedCin);
      setKyc(k => ({ ...k, cin: cinResult }));
      const coName = cinResult?.details?.companyName;
      if (coName) {
        setData(p => (p.hospitalName.trim() ? p : { ...p, hospitalName: coName }));
      }
      return;
    }
    if (cinPollRef.current !== expectedCin) return;
    cinPollRef.current = null;
    setKycBusy(b => ({ ...b, cin: false }));
    setKyc(k => ({
      ...k,
      cin: {
        verified: false,
        status: 'api_error',
        error: 'CIN lookup is still processing. Wait a moment and click Retry lookup.',
        cinNumber: expectedCin,
      },
    }));
  };

  const verifyCin = async () => {
    const c = (data.cinNumber || '').toUpperCase();
    if (!c || c.length !== 21 || !CIN_RX.test(c)) return;
    const gen = ++cinVerifyGen.current;
    setKyc(k => ({ ...k, cin: null }));
    setKycBusy(b => ({ ...b, cin: true }));
    let keepBusy = false;
    try {
      for (let attempt = 0; attempt <= KYC_AUTO_RETRIES; attempt += 1) {
        if (gen !== cinVerifyGen.current) return;
        if (attempt > 0) await sleep(900 * attempt);
        const r = await hospitalAPI.verifyCIN(c, applicationId);
        if (gen !== cinVerifyGen.current) return;
        const cinResult = parseKycApiResponse(r, 'cinNumber', c);
        if (cinResult?.status === 'pending') {
          setKyc(k => ({ ...k, cin: cinResult }));
          keepBusy = true;
          pollCinUntilDone(c);
          return;
        }
        if (cinResult && kycResultHasData('cin', cinResult)) {
          setKyc(k => ({ ...k, cin: cinResult }));
          const coName = cinResult?.details?.companyName;
          if (coName && (cinResult.verified || cinResult.status === 'partial')) {
            setData(p => (p.hospitalName.trim() ? p : { ...p, hospitalName: coName }));
          }
          return;
        }
        const canRetry = attempt < KYC_AUTO_RETRIES && isTransientKycFailure(cinResult, r?.error);
        if (!canRetry) {
          if (cinResult) setKyc(k => ({ ...k, cin: cinResult }));
          return;
        }
      }
    } finally {
      if (gen === cinVerifyGen.current && !keepBusy) setKycBusy(b => ({ ...b, cin: false }));
    }
  };

  const verifyBank = async () => {
    const ifs = (data.ifscCode || '').toUpperCase();
    const acc = String(data.accountNumber || '').replace(/\D/g, '');
    if (!acc || acc.length < 6 || ifs.length !== 11 || !IFSC_RX.test(ifs)) return;
    const gen = ++bankVerifyGen.current;
    return runKycQueued(async () => {
      await runKycVerifyLoop({
        kind: 'bank',
        gen,
        genRef: bankVerifyGen,
        busyKey: 'bank',
        fieldKey: 'accountNumber',
        fieldValue: acc,
        apiCall: () => hospitalAPI.verifyBank(acc, ifs, data.accountHolderName || null, applicationId),
        onSuccess: (bankResult) => {
          setKyc(k => ({ ...k, bank: { ...bankResult, ifsc: ifs } }));
          const holderName = bankResult?.details?.nameAtBank;
          if (holderName && (bankResult.verified || bankResult.status === 'partial')) {
            setData(p => ({ ...p, accountHolderName: holderName }));
          }
        },
      });
    });
  };

  // ─── AUTO-VERIFY (May 18, 2026) ───────────────────────────────────────
  // The hospital staff doesn't have to hunt for a "Verify" button anymore.
  // The moment they finish typing a syntactically valid GST / PAN / IFSC,
  // we kick off the verification call after a short debounce. This makes
  // the form feel "alive" and shaves real seconds off the onboarding flow.

  // GST: auto-verify when format is valid; auto-set state from GST state-code prefix.
  // PAN is entered manually — never auto-filled from GSTIN.
  useEffect(() => {
    const timers = autoVerifyTimers.current;
    const g = (data.gstNumber || '').toUpperCase().trim();
    if (!g) {
      if (kyc.gst) setKyc(k => ({ ...k, gst: null }));
      return;
    }
    if (g.length !== 15 || !GST_RX.test(g)) return;
    const stateName = GST_STATE_CODES[g.substring(0, 2)];
    if (stateName && !data.state && STATES.includes(stateName)) {
      setData(p => ({ ...p, state: stateName, district: '', city: '', cityCode: '' }));
    }
    if (kycBusy.gst) return;
    if (kyc.gst?.verified && kyc.gst?.gstNumber === g) return;
    if (kyc.gst?.status === 'pending') return;
    clearTimeout(timers.gst);
    timers.gst = setTimeout(() => { verifyGst(); }, KYC_VERIFY_DEBOUNCE_MS);
    return () => clearTimeout(timers.gst);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.gstNumber]);

  // PAN: auto-verify once 10 valid chars are typed.
  useEffect(() => {
    const timers = autoVerifyTimers.current;
    const p = (data.panNumber || '').toUpperCase();
    if (p.length !== 10 || !PAN_RX.test(p)) return;
    if (kycBusy.pan) return;
    if (kyc.pan?.verified && kyc.pan?.panNumber === p) return;
    if (kyc.pan?.status === 'pending') return;
    clearTimeout(timers.pan);
    timers.pan = setTimeout(() => { verifyPan(); }, KYC_VERIFY_DEBOUNCE_MS);
    return () => clearTimeout(timers.pan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.panNumber]);

  // Re-verify stale KYC when entering Legal step (resume / saved api_error).
  useEffect(() => {
    if (step !== 2) {
      kycStepRefreshDone.current.step2 = false;
      return;
    }
    if (kycStepRefreshDone.current.step2) return;
    kycStepRefreshDone.current.step2 = true;
    const t = setTimeout(() => {
      const g = (data.gstNumber || '').toUpperCase().trim();
      if (g.length === 15 && GST_RX.test(g) && !(kyc.gst?.verified && kyc.gst?.gstNumber === g)) verifyGst();
      const p = (data.panNumber || '').toUpperCase().trim();
      if (p.length === 10 && PAN_RX.test(p) && !(kyc.pan?.verified && kyc.pan?.panNumber === p)) verifyPan();
      const c = (data.cinNumber || '').toUpperCase().trim();
      if (c.length === 21 && CIN_RX.test(c) && !(kyc.cin?.verified && kyc.cin?.cinNumber === c) && kyc.cin?.status !== 'pending') verifyCin();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, applicationId]);

  // CIN: optional — auto-verify when 21 valid chars are typed.
  useEffect(() => {
    const timers = autoVerifyTimers.current;
    const c = (data.cinNumber || '').toUpperCase().trim();
    if (!c) {
      if (kyc.cin) setKyc(k => ({ ...k, cin: null }));
      return;
    }
    if (c.length !== 21 || !CIN_RX.test(c)) return;
    if (kycBusy.cin) return;
    if (kyc.cin?.verified && kyc.cin?.cinNumber === c) return;
    if (kyc.cin?.status === 'format_verified' && kyc.cin?.cinNumber === c) return;
    if (kyc.cin?.status === 'pending' && kyc.cin?.cinNumber === c) return;
    clearTimeout(timers.cin);
    timers.cin = setTimeout(() => { verifyCin(); }, KYC_VERIFY_DEBOUNCE_MS + 200);
    return () => clearTimeout(timers.cin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.cinNumber]);

  // Resume / refresh while a CIN lookup was still in progress on the server.
  useEffect(() => {
    const c = (data.cinNumber || '').toUpperCase().trim();
    if (!applicationId || c.length !== 21 || !CIN_RX.test(c)) return;
    if (kyc.cin?.status !== 'pending' || kyc.cin?.cinNumber !== c) return;
    if (kycBusy.cin || cinPollRef.current === c) return;
    setKycBusy(b => ({ ...b, cin: true }));
    pollCinUntilDone(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, data.cinNumber, kyc.cin?.status, kyc.cin?.cinNumber]);

  // IFSC: free public lookup → auto-fill bankName + branchName instantly.
  // Then, if accountNumber is also present, kick off bank verification too.
  useEffect(() => {
    const timers = autoVerifyTimers.current;
    const ifs = (data.ifscCode || '').toUpperCase();
    setIfscLookup(null);
    if (ifs.length !== 11 || !IFSC_RX.test(ifs)) return;
    clearTimeout(timers.ifsc);
    timers.ifsc = setTimeout(async () => {
      setIfscBusy(true);
      const info = await lookupIfsc(ifs);
      setIfscBusy(false);
      if (!info) return;
      setIfscLookup(info);
      // Auto-fill bank + branch (don't overwrite if user already typed)
      setData(p => ({
        ...p,
        bankName:   p.bankName   || info.BANK   || '',
        branchName: p.branchName || info.BRANCH || '',
      }));
    }, 500);
    return () => clearTimeout(timers.ifsc);
  }, [data.ifscCode]);

  // Bank: auto-verify when both account # (≥6) AND IFSC (11 valid) are set.
  useEffect(() => {
    const timers = autoVerifyTimers.current;
    const ifs = (data.ifscCode || '').toUpperCase();
    const acc = String(data.accountNumber || '').replace(/\D/g, '');
    if (!acc || acc.length < 6) return;
    if (ifs.length !== 11 || !IFSC_RX.test(ifs)) return;
    if (kycBusy.bank) return;
    if (kyc.bank?.verified && kyc.bank?.ifsc === ifs && String(kyc.bank?.accountNumber || '').slice(-4) === acc.slice(-4)) return;
    if (kyc.bank?.status === 'pending') return;
    clearTimeout(timers.bank);
    timers.bank = setTimeout(() => { verifyBank(); }, KYC_VERIFY_DEBOUNCE_MS);
    return () => clearTimeout(timers.bank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.accountNumber, data.ifscCode]);

  // Re-verify bank when entering bank step (resume / stale error).
  useEffect(() => {
    if (step !== 3) {
      kycStepRefreshDone.current.step3 = false;
      return;
    }
    if (kycStepRefreshDone.current.step3) return;
    kycStepRefreshDone.current.step3 = true;
    const t = setTimeout(() => {
      const ifs = (data.ifscCode || '').toUpperCase().trim();
      const acc = String(data.accountNumber || '').replace(/\D/g, '');
      if (!acc || acc.length < 6 || ifs.length !== 11 || !IFSC_RX.test(ifs)) return;
      const verified = kyc.bank?.verified
        && kyc.bank?.ifsc === ifs
        && String(kyc.bank?.accountNumber || '').slice(-4) === acc.slice(-4);
      if (!verified && kyc.bank?.status !== 'pending') verifyBank();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, applicationId]);

  // ─── EMAIL VERIFICATION ───────────────────────────────────────────────
  
  const sendVerificationEmail = async () => {
    if (!data.email.trim() || !data.email.includes('@')) {
      setError('Valid email is required');
      return;
    }
    if (!data.contactName.trim()) {
      setError('Contact name is required');
      return;
    }
    
    setVerifying(true);
    setError('');
    
    try {
      const r = await hospitalAPI.verifyEmail(data.email, data.contactName);
      if (r.error) {
        if (r.data?.applicationId) {
          setError(`Application already exists: ${r.data.applicationId}. Status: ${r.data.status}. Use "Resume application" below with your Application ID.`);
          setResumeAppIdInput(r.data.applicationId);
        } else {
          setError(r.error);
        }
        setVerifying(false);
        return;
      }
      
      setVerificationStep('otp');
      setResendTimer(60);
      setSaveMessage(`OTP sent to ${r.email || data.email}`);
    } catch (err) {
      setError('Failed to send verification email');
    }
    
    setVerifying(false);
  };
  
  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Enter 6-digit OTP');
      return;
    }
    
    setVerifying(true);
    setError('');
    
    try {
      const r = await hospitalAPI.verifyOTP(data.email, otp, data.contactName, data.hospitalName);
      if (r.error) {
        setError(r.error);
        setVerifying(false);
        return;
      }
      
      const appId = r.data?.applicationId || r.applicationId;
      const sess  = r.data?.sessionToken || r.sessionToken;
      setApplicationId(appId);
      setHospitalAppSession(sess, data.email, appId);
      setVerificationStep('verified');
      setStep(1);
      setSaveMessage(`Application created: ${appId}. Save this number!`);
    } catch (err) {
      setError('Verification failed');
    }
    
    setVerifying(false);
  };
  
  // ─── SAVE DRAFT ────────────────────────────────────────────────────────
  
  const saveDraft = async (isFinal = false, stepOverride = null) => {
    loadHospitalAppSession();
    if (!applicationId) {
      setError('Application ID not found. Please verify email first.');
      return false;
    }
    if (data.email) {
      const sess = loadHospitalAppSession();
      if (!sess.email) setHospitalAppSession(sess.token, data.email);
    }

    const stepToSave = stepOverride != null ? stepOverride : step;
    setSaving(true);
    setError('');
    setSaveMessage('');

    try {
      const formData = buildDraftFormData();
      const r = await hospitalAPI.saveDraft(applicationId, stepToSave, formData, isFinal);
      if (r.error) {
        const msg = String(r.error || '');
        if (/session|otp|verify email/i.test(msg)) {
          setError(`${msg} — use “Resume application” on Step 1 and verify with the email OTP.`);
        } else {
          setError(msg || 'Could not save draft');
        }
        return false;
      }

      if (r.data?.sessionToken) {
        setHospitalAppSession(r.data.sessionToken, data.email || loadHospitalAppSession().email, applicationId);
      }

      if (isFinal) {
        setSubmitted(true);
        setSaveMessage('Application submitted successfully!');
        clearHospitalAppSession();
      } else {
        const displayStep = stepToSave + 1;
        setSaveMessage(
          `Saved at Step ${displayStep} of ${STEPS.length}. Resume anytime with ID: ${applicationId}`
        );
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
      }
      return true;
    } catch (_) {
      setError('Failed to save. Check your connection and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const validateStep = () => {
    setError('');
    switch(step) {
      case 1:
        if (!data.hospitalName.trim()) return 'Hospital name is required';
        if (!data.contactName.trim()) return 'Contact person name is required';
        if (!data.email.trim() || !data.email.includes('@')) return 'Valid email is required';
        if (!data.phone.trim() || data.phone.length < 10) return 'Valid phone number is required';
        if (!data.address.trim()) return 'Address is required';
        if (!data.mandal?.trim() && !data.city?.trim()) return 'Mandal / Town is required — select from the dropdown';
        if (!data.district.trim()) return 'District is required — select from the dropdown';
        if (!data.state.trim()) return 'State is required — select from the dropdown';
        if (data.latitude == null || data.longitude == null)
          return 'Please pin your hospital location on the map — patients use this to find you';
        if (!data.ownerName.trim()) return 'Authorized person / owner name is required';
        if (!data.ownerPhone.trim()) return 'Authorized person phone is required';
        break;
      case 2:
        if (!data.registrationNumber.trim()) return 'Registration number is required';
        if (data.gstNumber.trim()) {
          if (data.gstNumber.length !== 15) return 'GST must be 15 characters when provided';
          if (!GST_RX.test(data.gstNumber.toUpperCase())) return 'Invalid GST format';
        }
        if (data.panNumber.trim()) {
          if (data.panNumber.length !== 10) return 'PAN must be 10 characters when provided';
          if (!PAN_RX.test(data.panNumber.toUpperCase())) return 'Invalid PAN format';
        }
        if (data.cinNumber.trim()) {
          if (data.cinNumber.length !== 21) return 'CIN must be 21 characters when provided';
          if (!CIN_RX.test(data.cinNumber.toUpperCase())) return 'Invalid CIN format';
        }
        break;
      case 3:
        if (!data.accountHolderName.trim()) return 'Account holder name is required';
        if (!data.accountNumber.trim()) return 'Account number is required';
        if (!data.ifscCode.trim() || data.ifscCode.length !== 11) return 'Valid 11-character IFSC code is required';
        if (!data.bankName.trim()) return 'Bank name is required';
        break;
      case 4:
        if (!data.declarationAgreed) return 'Please accept the declaration';
        break;
      default: break;
    }
    return null;
  };

  const nextStep = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    if (step < 4) {
      const newStep = step + 1;
      if (applicationId) {
        const ok = await saveDraft(false, newStep);
        if (!ok) return;
      }
      setStep(newStep);
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(s => s - 1);
  };

  const handleSubmit = async () => {
    if (!applicationId) {
      setError('Application ID not found');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      // Documents were already uploaded individually as the user picked them
      // (handleFileChange uploads + persists the URL on every selection).
      // Here we just collect the URLs already known to the client. As a
      // last-ditch fallback we still upload anything that somehow still
      // carries a raw File object (e.g. failed earlier upload that the user
      // retried during the same submit click).
      const documentUrls = {};
      for (const [key, doc] of Object.entries(data.documents || {})) {
        if (!doc) continue;
        if (doc.url) {
          documentUrls[key] = doc.url;
        } else if (doc.file) {
          const url = await hospitalAPI.uploadDocument(applicationId, key, doc.file);
          if (url) documentUrls[key] = url;
        }
      }
      
      // Submit final application
      const formData = {
        applicationId,
        hospitalName: data.hospitalName,
        contactName: data.contactName,
        email: data.email,
        phone: data.phone,
        emergencyPhone: data.emergencyPhone || data.phone,
        hospitalType: data.hospitalType,
        address: data.address,
        city: data.city,
        mandal: data.mandal || data.city,
        district: data.district,
        state: data.state,
        cityCode: data.cityCode,
        locality: data.locality,
        pincode: data.pincode,
        website: data.website,
        latitude: data.latitude,
        longitude: data.longitude,
        ownerName: data.ownerName,
        ownerDesignation: data.ownerDesignation,
        ownerPhone: data.ownerPhone,
        registrationNumber: data.registrationNumber,
        registrationDate: data.registrationDate,
        gstNumber: data.gstNumber,
        panNumber: data.panNumber,
        cinNumber: data.cinNumber,
        ...noBedsFormFields(),
        // Patient-trust fields (May 25, 2026)
        establishmentYear: data.establishmentYear ? parseStoredInt(data.establishmentYear) || null : null,
        about:             data.about || '',
        // OP timings
        opStart:      data.is24x7 ? '00:00' : (data.opStart || ''),
        opEnd:        data.is24x7 ? '23:59' : (data.opEnd   || ''),
        opFee:        parseStoredInt(data.opFee),
        is24x7:       !!data.is24x7,
        hasEmergency: !!data.hasEmergency,
        languagesSpoken:   Array.isArray(data.languagesSpoken) ? data.languagesSpoken : [],
        amenities:         data.amenities || {},
        bankDetails: {
          accountHolderName: data.accountHolderName,
          accountNumber: data.accountNumber,
          ifscCode: data.ifscCode,
          bankName: data.bankName,
          branchName: data.branchName,
          upiId: data.upiId,
        },
        documents: documentUrls,
        kyc: {
          gst: kyc.gst || null,
          pan: kyc.pan || null,
          bank: kyc.bank || null,
          cin: kyc.cin || null,
        },
      };
      
      const r = await hospitalAPI.submitApplication(formData);
      
      if (r.error) {
        setError(r.error);
      } else {
        setSubmitted(true);
        setSaveMessage('Application submitted! Check email for updates.');
      }
    } catch (e) {
      setError('Failed to submit application. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--auth-page-bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div style={{ width:'100%', maxWidth:480, textAlign:'center' }}>
          <div style={{ width:100, height:100, borderRadius:'50%', background:'rgba(0,212,170,0.1)', border:'2px solid rgba(0,212,170,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px' }}>
            <MdCheckCircle style={{ fontSize:56, color:'#00d4aa' }} />
          </div>
          <h1 style={{ fontSize:28, fontWeight:800, color:'var(--text-primary)', marginBottom:12 }}>Application Submitted!</h1>
          <p style={{ fontSize:15, color:'var(--text-secondary)', lineHeight:1.7, marginBottom:28 }}>
            Thank you for applying to join HCEN Network. Our compliance team will review your KYC documents and pinned location.<br/><br/>
            {applicationId && <><strong style={{ color:'#00d4aa' }}>Application ID:</strong> {applicationId}<br/></>}
            You will receive login credentials via email within 24–48 hours once approved.
          </p>
          <div style={{
            padding:14, borderRadius:10, marginBottom:24, textAlign:'left',
            background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.2)',
            fontSize:13, color:'var(--text-secondary)', lineHeight:1.7,
          }}>
            <strong style={{ color:'#a5b4fc' }}>What happens next?</strong><br/>
            1. Document & KYC review<br/>
            2. Location verification on map<br/>
            3. Approval email with hospital dashboard login
          </div>
          <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
            <Link to="/login" className="btn btn-primary">Go to Login</Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Loading state when resuming
  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--auth-page-bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div style={{ textAlign:'center', color:'var(--text-secondary)' }}>
          <div style={{ fontSize:48, marginBottom:16 }}>⏳</div>
          <div>Loading your application...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page auth-page--scroll" style={{ display:'block', padding:'20px 0' }}>
      <div className="apply-registration">
        {/* Header */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20,
          padding:'18px 20px', borderRadius:16,
          background:'linear-gradient(135deg, rgba(0,212,170,0.08), rgba(99,102,241,0.06))',
          border:'1px solid var(--border-color)',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <img src="/hcen_logo.png" alt="HCEN" style={{ width:52, height:52, borderRadius:14 }} />
            <div>
              <h1 style={{ fontSize:24, fontWeight:800, color:'var(--text-primary)', margin:0 }}>Join HCEN Network</h1>
              <p style={{ fontSize:13, color:'var(--text-secondary)', margin:'4px 0 0' }}>
                {applicationId ? `Application ${applicationId}` : 'Premium hospital onboarding — verified in 24–48 hours'}
              </p>
            </div>
          </div>
          <Link to="/login" style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text-secondary)', fontSize:14, textDecoration:'none' }}>
            <MdArrowBack /> Back to Login
          </Link>
        </div>

        {/* Trust strip */}
        <div style={{
          display:'flex', flexWrap:'wrap', gap:10, marginBottom:18, justifyContent:'center',
        }}>
          {['🔒 Secure onboarding', '📧 Verified email', '📍 GPS location', 'Registry checks'].map(t => (
            <span key={t} style={{
              fontSize:11, fontWeight:700, padding:'5px 12px', borderRadius:999,
              background:'var(--surface-subtle)', border:'1px solid var(--border-color)',
              color:'var(--text-secondary)',
            }}>{t}</span>
          ))}
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-muted)', marginBottom:6 }}>
            <span>Step {step + 1} of {STEPS.length}</span>
            <span style={{ color:'#00d4aa', fontWeight:700 }}>{Math.round(((step) / (STEPS.length - 1)) * 100)}% complete</span>
          </div>
          <div style={{ height:4, borderRadius:4, background:'var(--border-color)', overflow:'hidden' }}>
            <div style={{
              height:'100%', borderRadius:4,
              background:'linear-gradient(90deg,#00d4aa,#4f8ef7)',
              width:`${Math.round(((step) / (STEPS.length - 1)) * 100)}%`,
              transition:'width 0.35s ease',
            }} />
          </div>
        </div>

        {/* Progress */}
        <div className="steps-scroll" style={{ display:'flex', justifyContent:'flex-start', marginBottom:32, minWidth:0 }}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={{ display:'flex', alignItems:'center' }}>
              <div style={{ 
                display:'flex', flexDirection:'column', alignItems:'center', gap:6,
                opacity: step >= s.id ? 1 : 0.4
              }}>
                <div style={{ 
                  width:44, height:44, borderRadius:'50%', 
                  background: step >= s.id ? 'rgba(0,212,170,0.2)' : 'var(--surface-subtle)',
                  border: `2px solid ${step >= s.id ? '#00d4aa' : 'var(--text-muted)'}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color: step >= s.id ? '#00d4aa' : 'var(--text-muted)',
                  fontSize:20
                }}>
                  {step > s.id ? <MdCheckCircle /> : s.icon}
                </div>
                <span style={{ fontSize:11, color: step >= s.id ? '#00d4aa' : 'var(--text-muted)', fontWeight:600, whiteSpace:'nowrap' }}>{s.title}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ width:40, height:2, background: step > s.id ? '#00d4aa' : 'var(--border-color-hover)', margin:'0 8px' }} />
              )}
            </div>
          ))}
        </div>

        {/* Admin-flagged fixes banner (shown when resuming a needs_resubmission application) */}
        {requestedFixes.length > 0 && appStatus === 'needs_resubmission' && (
          <div style={{
            padding:'16px 18px', marginBottom:20, borderRadius:12,
            background:'rgba(168,85,247,0.08)', border:'1px solid rgba(168,85,247,0.35)',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontWeight:800, color:'#c4b5fd', marginBottom:10 }}>
              <MdWarning /> Reviewer requested {requestedFixes.length} change{requestedFixes.length === 1 ? '' : 's'}
            </div>
            <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:10 }}>
              Please correct the following items and resubmit. Your previously entered data has been preserved.
            </div>
            <ul style={{ margin:0, paddingLeft:22, fontSize:13, color:'var(--text-primary)' }}>
              {requestedFixes.map((f, i) => (
                <li key={i} style={{ marginBottom:6 }}>
                  <strong>{f.label || f.field}:</strong>{' '}
                  <span style={{ color:'#fde68a', fontStyle:'italic' }}>{f.issue}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Save Success Message */}
        {saveMessage && (
          <div style={{ 
            padding:'14px 18px', background:'rgba(0,212,170,0.1)', border:'1px solid rgba(0,212,170,0.3)', 
            borderRadius:12, color:'#00d4aa', fontSize:14, marginBottom:20, display:'flex', alignItems:'center', gap:10
          }}>
            <MdCheckCircle /> {saveMessage}
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div style={{ 
            padding:'14px 18px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', 
            borderRadius:12, color:'#f87171', fontSize:14, marginBottom:20, display:'flex', alignItems:'center', gap:10
          }}>
            <MdWarning /> {error}
          </div>
        )}

        {/* Step Content */}
        <div className="glass-card" style={{ padding:32 }}>
          
          {/* Step 0: Email Verification */}
          {step === 0 && (
            <div>
              <h2 style={{ fontSize:20, fontWeight:700, marginBottom:24, display:'flex', alignItems:'center', gap:10 }}>
                <MdEmail style={{ color:'#00d4aa' }} /> Verify Your Email
              </h2>
              
              {verificationStep === 'email' && (
                <div style={{ maxWidth:400, margin:'0 auto' }}>
                  <p style={{ color:'var(--text-secondary)', marginBottom:20, textAlign:'center' }}>
                    Enter your email and contact name. We'll send you a verification code to begin your application.
                  </p>
                  
                  <div className="form-group" style={{ marginBottom:16 }}>
                    <label className="form-label">Contact Person Name *</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      value={data.contactName} 
                      onChange={e => updateField('basic', 'contactName', e.target.value)} 
                      placeholder="e.g., Dr. Rajesh Kumar"
                    />
                  </div>
                  
                  <div className="form-group" style={{ marginBottom:20 }}>
                    <label className="form-label">Email Address *</label>
                    <input 
                      type="email" 
                      className="form-input" 
                      value={data.email} 
                      onChange={e => updateField('basic', 'email', e.target.value)} 
                      placeholder="hospital@example.com"
                    />
                  </div>
                  
                  <button 
                    onClick={sendVerificationEmail}
                    disabled={verifying}
                    style={{ 
                      width:'100%', padding:'14px', background:'linear-gradient(135deg,#00d4aa,#008080)', 
                      border:'none', borderRadius:10, color:'#000', fontWeight:700, fontSize:16,
                      cursor: verifying ? 'not-allowed' : 'pointer', opacity: verifying ? 0.7 : 1
                    }}
                  >
                    {verifying ? 'Sending...' : 'Send Verification Code'}
                  </button>
                  
                  {/* Resume existing application — OTP required for security */}
                  <div style={{
                    marginTop:18,
                    padding:14,
                    background: resumePanelFocus ? 'rgba(217,119,6,0.08)' : 'var(--surface-subtle)',
                    border: resumePanelFocus ? '2px solid rgba(217,119,6,0.45)' : '1px solid var(--border-color)',
                    borderRadius:10,
                    transition:'border-color 0.2s, background 0.2s',
                  }}>
                    <p style={{ margin:0, marginBottom:10, fontSize:13, color:'var(--text-secondary)', textAlign:'center' }}>
                      <strong>Resume an existing application?</strong> Enter your Application ID — each hospital has its own ID. We verify with an email OTP every time.
                    </p>

                    {resumeStep === 'idle' ? (
                      <div style={{ display:'flex', gap:8 }}>
                        <input
                          type="text"
                          className="form-input"
                          value={resumeAppIdInput}
                          onChange={e => setResumeAppIdInput(e.target.value.toUpperCase().replace(/\s/g, ''))}
                          placeholder="e.g., APP-260000000010"
                          style={{ flex:1, fontFamily:'monospace', letterSpacing:0.5 }}
                          onKeyDown={e => { if (e.key === 'Enter') requestResumeOtp(); }}
                        />
                        <button
                          type="button"
                          onClick={requestResumeOtp}
                          disabled={verifying || !resumeAppIdInput.trim()}
                          style={{ padding:'10px 16px', background:'rgba(0,212,170,0.15)', border:'1px solid rgba(0,212,170,0.4)', borderRadius:8, color:'#00d4aa', fontWeight:700, cursor: verifying ? 'not-allowed' : 'pointer', whiteSpace:'nowrap', opacity: verifying ? 0.7 : 1 }}
                        >
                          {verifying ? 'Sending…' : 'Send OTP'}
                        </button>
                      </div>
                    ) : (
                      <div style={{ maxWidth:400, margin:'0 auto' }}>
                        <p style={{ fontSize:13, color:'var(--text-secondary)', textAlign:'center', marginBottom:12 }}>
                          Enter the 6-digit code sent to <strong>{resumeEmailMasked}</strong> for{' '}
                          <strong style={{ fontFamily:'monospace' }}>{pendingResumeId}</strong>
                        </p>
                        <input
                          type="text"
                          className="form-input"
                          value={resumeOtp}
                          onChange={e => setResumeOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="123456"
                          maxLength={6}
                          style={{ textAlign:'center', fontSize:22, letterSpacing:8, fontFamily:'monospace', marginBottom:12 }}
                          onKeyDown={e => { if (e.key === 'Enter' && resumeOtp.length === 6) verifyResumeOtp(); }}
                        />
                        <button
                          type="button"
                          onClick={verifyResumeOtp}
                          disabled={verifying || resumeOtp.length !== 6}
                          style={{
                            width:'100%', padding:'12px', marginBottom:10,
                            background:'linear-gradient(135deg,#00d4aa,#008080)',
                            border:'none', borderRadius:10, color:'#000', fontWeight:700, fontSize:15,
                            cursor: verifying || resumeOtp.length !== 6 ? 'not-allowed' : 'pointer',
                            opacity: verifying || resumeOtp.length !== 6 ? 0.7 : 1,
                          }}
                        >
                          {verifying ? 'Verifying…' : 'Verify & Resume Application'}
                        </button>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
                          <button type="button" onClick={cancelResume} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', textDecoration:'underline' }}>
                            Use different ID
                          </button>
                          <button
                            type="button"
                            onClick={requestResumeOtp}
                            disabled={resumeResendTimer > 0 || verifying}
                            style={{ background:'none', border:'none', color: resumeResendTimer > 0 ? 'var(--text-muted)' : '#00d4aa', cursor: resumeResendTimer > 0 ? 'not-allowed' : 'pointer' }}
                          >
                            {resumeResendTimer > 0 ? `Resend in ${resumeResendTimer}s` : 'Resend code'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {verificationStep === 'otp' && (
                <div style={{ maxWidth:400, margin:'0 auto', textAlign:'center' }}>
                  <p style={{ color:'var(--text-secondary)', marginBottom:20 }}>
                    Enter the 6-digit code sent to <strong>{data.email}</strong>
                  </p>
                  
                  <div className="form-group" style={{ marginBottom:20 }}>
                    <input 
                      type="text" 
                      className="form-input" 
                      value={otp} 
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0,6))}
                      placeholder="123456"
                      maxLength={6}
                      style={{ textAlign:'center', fontSize:24, letterSpacing:8, fontFamily:'monospace' }}
                    />
                  </div>
                  
                  <button 
                    onClick={verifyOTP}
                    disabled={verifying || otp.length !== 6}
                    style={{ 
                      width:'100%', padding:'14px', background:'linear-gradient(135deg,#00d4aa,#008080)', 
                      border:'none', borderRadius:10, color:'#000', fontWeight:700, fontSize:16,
                      cursor: (verifying || otp.length !== 6) ? 'not-allowed' : 'pointer', 
                      opacity: (verifying || otp.length !== 6) ? 0.7 : 1,
                      marginBottom:16
                    }}
                  >
                    {verifying ? 'Verifying...' : 'Verify & Continue'}
                  </button>
                  
                  <button 
                    onClick={sendVerificationEmail}
                    disabled={resendTimer > 0 || verifying}
                    style={{ 
                      background:'none', border:'none', color:resendTimer > 0 ? 'var(--text-muted)' : '#00d4aa',
                      cursor: resendTimer > 0 ? 'not-allowed' : 'pointer', fontSize:14
                    }}
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div>
              <h2 style={{ fontSize:20, fontWeight:700, marginBottom:24, display:'flex', alignItems:'center', gap:10 }}>
                <MdLocalHospital style={{ color:'#00d4aa' }} /> Hospital Basic Information
              </h2>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16 }}>
                <div className="form-group">
                  <label className="form-label">Hospital Name *</label>
                  <input type="text" className="form-input" value={data.hospitalName} 
                    onChange={e => updateField('basic', 'hospitalName', e.target.value)} 
                    placeholder="e.g., Apollo Hospitals" />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Person Name *</label>
                  <input type="text" className="form-input" value={data.contactName} 
                    onChange={e => updateField('basic', 'contactName', e.target.value)} 
                    placeholder="e.g., Dr. Rajesh Kumar" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input type="email" className="form-input" value={data.email} 
                    onChange={e => updateField('basic', 'email', e.target.value)} 
                    placeholder="admin@hospital.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number *</label>
                  <input type="tel" className="form-input" value={data.phone} 
                    onChange={e => updateField('basic', 'phone', e.target.value)} 
                    placeholder="+91 9876543210" maxLength={15} />
                </div>
                <div className="form-group">
                  <label className="form-label">Emergency Phone</label>
                  <input type="tel" className="form-input" value={data.emergencyPhone} 
                    onChange={e => updateField('basic', 'emergencyPhone', e.target.value)} 
                    placeholder="+91 9876543210" maxLength={15} />
                </div>
                <div className="form-group">
                  <label className="form-label">Hospital Type *</label>
                  <select className="form-input" value={data.hospitalType} 
                    onChange={e => updateField('basic', 'hospitalType', e.target.value)}>
                    {HOSPITAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn:'1 / -1' }}>
                  <label className="form-label">Complete Address *</label>
                  <textarea className="form-input" rows={2} value={data.address} 
                    onChange={e => updateField('basic', 'address', e.target.value)} 
                    placeholder="Street address, building name, landmark..." />
                </div>

                {/* ── Pin location on map (required) ───────────────────── */}
                <div className="form-group" style={{ gridColumn:'1 / -1' }}>
                  <label className="form-label" style={{ display:'flex', alignItems:'center', gap:6 }}>
                    📍 Pin Hospital Location on Map *
                    <span style={{ marginLeft:4, fontSize:11, fontWeight:400, color:'var(--text-muted)' }}>
                      — patients use this to find your hospital
                    </span>
                  </label>
                  <div style={{
                    background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.2)',
                    borderRadius:8, padding:'8px 12px', marginBottom:10, fontSize:12,
                    color:'rgba(239,100,100,0.9)',
                  }}>
                    🚨 <strong>Important:</strong> Search for your hospital, click "Use my location", or click on the map to drop a pin. Drag the red pin to fine-tune the exact entrance.
                  </div>
                  <LocationMapPicker
                    value={(data.latitude != null && data.longitude != null) ? {
                      lat: data.latitude,
                      lng: data.longitude,
                      address: data.address,
                      city: data.city,
                      district: data.district,
                      state: data.state,
                      pincode: data.pincode,
                    } : null}
                    onChange={(loc) => {
                      if (!loc) {
                        setData(p => ({
                          ...p,
                          latitude: null,
                          longitude: null,
                          address: '',
                          pincode: '',
                          state: '',
                          district: '',
                          city: '',
                          mandal: '',
                          locality: '',
                          cityCode: '',
                        }));
                        return;
                      }
                      const resolved = resolveFromGeocode(loc);
                      const liveGps = !!loc._liveGps;
                      setData(p => ({
                        ...p,
                        latitude:  loc.lat != null ? loc.lat : p.latitude,
                        longitude: loc.lng != null ? loc.lng : p.longitude,
                        address:   loc.address ?? (liveGps ? '' : (loc.display ?? p.address)),
                        locality:  loc.locality ?? (liveGps ? '' : p.locality),
                        pincode:   (loc.pincode && /^[1-9]\d{5}$/.test(loc.pincode))
                          ? loc.pincode
                          : (liveGps ? '' : p.pincode),
                        state:     resolved.state || (liveGps ? '' : p.state),
                        district:  resolved.district || (liveGps ? '' : p.district),
                        city:      resolved.city || (liveGps ? '' : p.city),
                        mandal:    resolved.mandal || (liveGps ? '' : p.mandal),
                        cityCode:  resolved.cityCode || (liveGps ? '' : p.cityCode),
                      }));
                    }}
                    onSave={async () => { await saveDraft(false); }}
                    saving={saving}
                    height={320}
                  />
                </div>

                <div style={{ marginTop: 16, marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--text-primary)' }}>
                    📍 Official location (select only — matches patient app)
                  </div>
                  <LocationCascadeSelect
                    state={data.state}
                    district={data.district}
                    city={data.city}
                    mandal={data.mandal || data.city}
                    locality={data.locality}
                    onChange={(loc) => setData(p => ({ ...p, ...loc, city: loc.mandal || loc.city }))}
                  />
                  {data.cityCode && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                      Location ID: {data.cityCode}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">PIN Code</label>
                  <input type="text" className="form-input" value={data.pincode} 
                    onChange={e => updateField('basic', 'pincode', e.target.value)} maxLength={6} />
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input type="url" className="form-input" value={data.website} 
                    onChange={e => updateField('basic', 'website', e.target.value)} 
                    placeholder="https://www.hospital.com" />
                </div>
              </div>

              {/* ── Authorized Person / Hospital Owner ───────────────── */}
              <div style={{
                marginTop:24, padding:16, borderRadius:12,
                background:'rgba(99,102,241,0.06)', border:'1px solid rgba(99,102,241,0.2)',
              }}>
                <div style={{ fontWeight:700, fontSize:14, color:'#a5b4fc', marginBottom:6 }}>
                  👤 Authorized Person / Hospital Owner
                </div>
                <div style={{ fontSize:12.5, color:'var(--text-secondary)', marginBottom:14 }}>
                  Legally accountable signatory for this hospital. This may be the same as the
                  contact person above, or different (e.g. a managing director or trustee).
                  Used by HCEN for compliance verification against your PAN / Registration certificate.
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:14 }}>
                  <div className="form-group">
                    <label className="form-label">Authorized Person Name *</label>
                    <input type="text" className="form-input" value={data.ownerName}
                      onChange={e => updateField('basic', 'ownerName', e.target.value)}
                      placeholder="Full legal name as per PAN" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Designation *</label>
                    <select className="form-input" value={data.ownerDesignation}
                      onChange={e => updateField('basic', 'ownerDesignation', e.target.value)}>
                      {['Owner','Proprietor','Partner','Managing Director','Director','CEO','Trustee','Administrator','Authorized Signatory'].map(d =>
                        <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Authorized Person Phone *</label>
                    <input type="tel" className="form-input" value={data.ownerPhone}
                      onChange={e => updateField('basic', 'ownerPhone', e.target.value)}
                      placeholder="+91 9876543210" maxLength={15} />
                  </div>
                </div>
              </div>

              {/* ── Hospital Details · patient-trust fields (May 25, 2026) ── */}
              <div style={{
                marginTop:24, padding:16, borderRadius:12,
                background:'rgba(0,212,170,0.05)', border:'1px solid rgba(0,212,170,0.18)',
              }}>
                <div style={{ fontWeight:700, fontSize:14, color:'#5eead4', marginBottom:6 }}>
                  🏥 Hospital Details
                </div>
                <div style={{ fontSize:12.5, color:'var(--text-secondary)', marginBottom:14 }}>
                  These fields appear on your public hospital page — they help patients
                  trust you and choose you over competitors. Filling all of them gives
                  your listing a “Verified Premium” badge.
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:14 }}>
                  <div className="form-group">
                    <label className="form-label">Established Year</label>
                    <input type="number" className="form-input" value={data.establishmentYear}
                      onChange={e => updateField('basic', 'establishmentYear', e.target.value.replace(/\D/g,'').slice(0,4))}
                      placeholder="e.g., 1998" min="1900" max={new Date().getFullYear()} />
                  </div>
                  <div className="form-group" style={{ gridColumn:'1 / -1' }}>
                    <label className="form-label">
                      About the Hospital <span style={{ fontSize:11, color:'var(--text-muted)' }}>({(data.about||'').length}/300)</span>
                    </label>
                    <textarea className="form-input" rows={3} value={data.about} maxLength={300}
                      onChange={e => updateField('basic', 'about', e.target.value)}
                      placeholder="e.g., 50-bed multi-speciality hospital with 24x7 emergency, NICU, and an in-house diagnostic lab. Treating Tirupati patients since 1998." />
                  </div>
                </div>

                {/* On-site amenities */}
                <div style={{ marginTop:14 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)', marginBottom:8 }}>On-site Amenities</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {[
                      ['pharmacy',    '💊 Pharmacy'],
                      ['bloodBank',   '🩸 Blood Bank'],
                      ['lab24x7',     '🧪 24x7 Lab'],
                      ['ambulance',   '🚑 Ambulance'],
                      ['parking',     '🅿️ Parking'],
                      ['wheelchair',  '♿ Wheelchair Access'],
                      ['cafeteria',   '☕ Cafeteria'],
                      ['teleConsult', '💻 Tele-Consultation'],
                      ['homeVisit',   '🏠 Home Visit'],
                    ].map(([key, label]) => {
                      const on = !!data.amenities[key];
                      return (
                        <button key={key} type="button"
                          onClick={() => setData(p => ({ ...p, amenities: { ...p.amenities, [key]: !on } }))}
                          style={{
                            padding:'8px 12px', borderRadius:20, fontSize:12.5, fontWeight:600,
                            background: on ? 'rgba(0,212,170,0.18)' : 'var(--surface-subtle)',
                            border:    `1px solid ${on ? 'rgba(0,212,170,0.5)' : 'var(--surface-strong)'}`,
                            color:     on ? '#5eead4' : 'var(--text-secondary)',
                            cursor:    'pointer',
                          }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Languages spoken */}
                <div style={{ marginTop:14 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)', marginBottom:8 }}>Languages Spoken at Reception</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {['English','Hindi','Telugu','Tamil','Kannada','Malayalam','Marathi','Bengali','Gujarati','Punjabi','Urdu','Odia'].map(lang => {
                      const on = (data.languagesSpoken || []).includes(lang);
                      return (
                        <button key={lang} type="button"
                          onClick={() => setData(p => ({
                            ...p,
                            languagesSpoken: on
                              ? (p.languagesSpoken || []).filter(l => l !== lang)
                              : [ ...(p.languagesSpoken || []), lang ],
                          }))}
                          style={{
                            padding:'6px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                            background: on ? 'rgba(99,102,241,0.18)' : 'var(--surface-subtle)',
                            border:    `1px solid ${on ? 'rgba(99,102,241,0.5)' : 'var(--surface-strong)'}`,
                            color:     on ? '#a5b4fc' : 'var(--text-secondary)',
                            cursor:    'pointer',
                          }}>
                          {lang}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* OP timings & fees — admin Basic-Info expects these (May 25, 2026) */}
                <div style={{ marginTop:18 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)', marginBottom:8 }}>
                    OP (Out-Patient) Timings & Consultation Fee
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label className="form-label" style={{ fontSize:11.5 }}>OP Start</label>
                      <input type="time" className="form-input" value={data.opStart}
                        onChange={e => updateField('basic', 'opStart', e.target.value)}
                        disabled={data.is24x7} />
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label className="form-label" style={{ fontSize:11.5 }}>OP End</label>
                      <input type="time" className="form-input" value={data.opEnd}
                        onChange={e => updateField('basic', 'opEnd', e.target.value)}
                        disabled={data.is24x7} />
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label className="form-label" style={{ fontSize:11.5 }}>OP Fee (₹)</label>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" className="form-input"
                        value={data.opFee}
                        onChange={e => updateField('basic', 'opFee', digitsOnly(e.target.value, 6))}
                        onWheel={e => e.currentTarget.blur()}
                        placeholder="e.g., 500" />
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:14, marginTop:10, flexWrap:'wrap' }}>
                    <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12.5, color:'var(--text-secondary)', cursor:'pointer' }}>
                      <input type="checkbox" checked={data.is24x7}
                        onChange={e => updateField('basic', 'is24x7', e.target.checked)} />
                      Open 24 × 7 (no fixed OP hours)
                    </label>
                    <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12.5, color:'var(--text-secondary)', cursor:'pointer' }}>
                      <input type="checkbox" checked={data.hasEmergency}
                        onChange={e => updateField('basic', 'hasEmergency', e.target.checked)} />
                      24 × 7 Emergency / Casualty available
                    </label>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Step 2: Legal & KYC */}
          {step === 2 && (
            <div>
              <h2 style={{ fontSize:20, fontWeight:700, marginBottom:24, display:'flex', alignItems:'center', gap:10 }}>
                <MdVerified style={{ color:'#00d4aa' }} /> Legal & KYC Information
              </h2>
              
              <div style={{ padding:16, background:'rgba(0,212,170,0.05)', border:'1px solid rgba(0,212,170,0.15)', borderRadius:10, marginBottom:24 }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                  <MdInfo style={{ color:'#00d4aa', fontSize:20, flexShrink:0, marginTop:2 }} />
                  <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6 }}>
                    {kycLiveLookup?.liveLookup === false
                      ? 'GST, PAN, and CIN are optional but speed up approval. Bank details are format-checked as you type. Live government registry lookup runs when enabled on the server.'
                      : 'GST, PAN, and CIN are optional but speed up approval. Bank account is checked against government records when you finish typing.'}
                    {kycLiveLookup?.liveLookup === false && (
                      <div style={{ marginTop:8, padding:'8px 10px', borderRadius:8, background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.25)', color:'#fbbf24', fontSize:12 }}>
                        Live lookup is not connected on the server yet — only format validation is running. Contact HCEN admin to enable automatic name fetch on AWS Lambda.
                      </div>
                    )}
                    {kycLiveLookup?.ipWhitelistOk === false && kycLiveLookup?.whitelistHint && !kyc.gst?.verified && !kyc.pan?.verified && (
                      <div style={{ marginTop:8, padding:'8px 10px', borderRadius:8, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', color:'#fca5a5', fontSize:12 }}>
                        Server IP not whitelisted for CGPE — lookups may fail until fixed.
                        <div style={{ marginTop:4 }}>{HCEN_WHITELIST_HINT}</div>
                      </div>
                    )}
                    All documents must be valid and clearly visible. Falsified information will result in permanent ban from the platform.
                  </div>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16 }}>
                <div className="form-group">
                  <label className="form-label">Hospital Registration Number *</label>
                  <input type="text" className="form-input" value={data.registrationNumber} 
                    onChange={e => updateField('legal', 'registrationNumber', e.target.value.toUpperCase())} 
                    placeholder="e.g., MH-12345-2020" />
                </div>
                <div className="form-group">
                  <label className="form-label">Registration Date</label>
                  <input type="date" className="form-input" value={data.registrationDate} 
                    onChange={e => updateField('legal', 'registrationDate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">GST Number <span style={{ color:'var(--text-muted)', fontWeight:500 }}>(optional)</span></label>
                  <input type="text" className="form-input" value={data.gstNumber} maxLength={15}
                    onChange={e => {
                      ++gstVerifyGen.current;
                      updateField('legal', 'gstNumber', e.target.value.toUpperCase());
                      if (kyc.gst) setKyc(k => ({ ...k, gst: null }));
                    }}
                    placeholder="Leave blank if not registered for GST" />
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
                    Format: 2 digits state code + PAN + 3 digit suffix
                  </div>
                  <KycRegistryPanel busy={kycBusy.gst} result={data.gstNumber ? kyc.gst : null} onRetry={verifyGst} kind="gst" />
                </div>
                <div className="form-group">
                  <label className="form-label">PAN Number <span style={{ color:'var(--text-muted)', fontWeight:500 }}>(optional)</span></label>
                  <input type="text" className="form-input" value={data.panNumber} maxLength={10}
                    onChange={e => {
                      ++panVerifyGen.current;
                      updateField('legal', 'panNumber', e.target.value.toUpperCase());
                      if (kyc.pan) setKyc(k => ({ ...k, pan: null }));
                    }}
                    placeholder="Leave blank if not applicable" />
                  <KycRegistryPanel busy={kycBusy.pan} result={data.panNumber ? kyc.pan : null} onRetry={verifyPan} kind="pan" />
                </div>
                <div className="form-group" style={{ gridColumn:'1 / -1' }}>
                  <label className="form-label">CIN / MCA Number <span style={{ color:'var(--text-muted)', fontWeight:500 }}>(optional — Pvt Ltd / LLP)</span></label>
                  <input type="text" className="form-input" value={data.cinNumber} maxLength={21}
                    onChange={e => {
                      ++cinVerifyGen.current;
                      updateField('legal', 'cinNumber', e.target.value.toUpperCase());
                      if (kyc.cin) setKyc(k => ({ ...k, cin: null }));
                    }}
                    placeholder="e.g., U72900MH2019PTC123456" />
                  <KycRegistryPanel busy={kycBusy.cin} result={data.cinNumber ? kyc.cin : null} onRetry={verifyCin} kind="cin" />
                </div>

              </div>

              <KycSummaryPanel kyc={kyc} data={data} />
            </div>
          )}

          {/* Step 3: Bank Details */}
          {step === 3 && (
            <div>
              <h2 style={{ fontSize:20, fontWeight:700, marginBottom:24, display:'flex', alignItems:'center', gap:10 }}>
                <MdAccountBalance style={{ color:'#00d4aa' }} /> Bank Account Details
              </h2>
              <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:16, lineHeight:1.5 }}>
                Account verified via CGPE <strong>Bank Penny Drop</strong> (₹1 credited to confirm account holder name with NPCI).
              </p>
              
              <div style={{ padding:16, background:'rgba(0,212,170,0.05)', border:'1px solid rgba(0,212,170,0.15)', borderRadius:10, marginBottom:24 }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                  <MdInfo style={{ color:'#00d4aa', fontSize:20, flexShrink:0, marginTop:2 }} />
                  <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6 }}>
                    This account will be used for receiving patient consultation fees and settlements. 
                    Please ensure the account details are accurate to avoid payment delays.
                  </div>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16 }}>
                <div className="form-group">
                  <label className="form-label">Account Holder Name * (as per bank records)</label>
                  <input type="text" className="form-input" value={data.accountHolderName} 
                    onChange={e => updateField('bank', 'accountHolderName', e.target.value)} 
                    placeholder="e.g., Apollo Hospitals Pvt Ltd" />
                </div>
                <div className="form-group" style={{ gridColumn:'1 / -1' }}>
                  <label className="form-label">Account Number *</label>
                  <input type="text" className="form-input" value={data.accountNumber} 
                    onChange={e => {
                      ++bankVerifyGen.current;
                      updateField('bank', 'accountNumber', e.target.value.replace(/\D/g, ''));
                      if (kyc.bank) setKyc(k => ({ ...k, bank: null }));
                    }}
                    placeholder="e.g., 123456789012" />
                  <KycRegistryPanel busy={kycBusy.bank} result={kyc.bank} onRetry={verifyBank} kind="bank" />
                </div>
                <div className="form-group">
                  <label className="form-label">IFSC Code * (11 characters)</label>
                  <input type="text" className="form-input" value={data.ifscCode} maxLength={11}
                    onChange={e => {
                      ++bankVerifyGen.current;
                      updateField('bank', 'ifscCode', e.target.value.toUpperCase());
                      if (kyc.bank) setKyc(k => ({ ...k, bank: null }));
                    }}
                    placeholder="e.g., HDFC0000123" />
                  {/* IFSC public-API auto-detection hint (May 18, 2026) */}
                  {ifscBusy && (
                    <div style={{ marginTop:6, fontSize:11, color:'#a5b4fc', display:'flex', alignItems:'center', gap:6 }}>
                      <span className="anim-spin" style={{ width:10, height:10, border:'2px solid rgba(165,180,252,0.3)', borderTopColor:'#a5b4fc', borderRadius:'50%', display:'inline-block' }} />
                      Looking up bank from IFSC…
                    </div>
                  )}
                  {!ifscBusy && ifscLookup && (
                    <div style={{
                      marginTop:6, padding:'8px 10px', borderRadius:8,
                      background:'linear-gradient(135deg,rgba(79,142,247,0.10),rgba(99,102,241,0.04))',
                      border:'1px solid rgba(79,142,247,0.30)',
                      fontSize:11.5, color:'#a5b4fc', lineHeight:1.45,
                    }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, fontWeight:700, color:'#bfdbfe' }}>
                        <MdCheck style={{ fontSize:13 }} /> Auto-detected from IFSC
                      </div>
                      <div style={{ marginTop:2, color:'var(--text-secondary)' }}>
                        <strong>{ifscLookup.BANK}</strong>{ifscLookup.BRANCH ? ` · ${ifscLookup.BRANCH}` : ''}
                        {ifscLookup.CITY ? ` · ${ifscLookup.CITY}` : ''}
                      </div>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Bank Name *</label>
                  <input type="text" className="form-input" value={data.bankName} 
                    onChange={e => updateField('bank', 'bankName', e.target.value)} 
                    placeholder="e.g., HDFC Bank" />
                </div>
                <div className="form-group">
                  <label className="form-label">Branch Name</label>
                  <input type="text" className="form-input" value={data.branchName} 
                    onChange={e => updateField('bank', 'branchName', e.target.value)} 
                    placeholder="e.g., MG Road Branch" />
                </div>
                <div className="form-group">
                  <label className="form-label">UPI ID (optional)</label>
                  <input type="text" className="form-input" value={data.upiId} 
                    onChange={e => updateField('bank', 'upiId', e.target.value)} 
                    placeholder="e.g., hospital@upi" />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review — declaration checkbox only */}
          {step === 4 && (
            <div>
              <h2 style={{ fontSize:20, fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
                <MdCheckCircle style={{ color:'#00d4aa' }} /> Review & Submit
              </h2>
              <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:24, lineHeight:1.6 }}>
                Please confirm the declaration below to submit your application. HCEN may request additional documents or a partnership agreement before or after approval.
              </p>

              <div style={{ padding:20, background:'rgba(99,102,241,0.05)', border:'1px solid rgba(99,102,241,0.2)', borderRadius:12 }}>
                <h4 style={{ fontSize:14, fontWeight:700, color:'#818cf8', marginBottom:12 }}>Declaration</h4>
                <label style={{ display:'flex', alignItems:'flex-start', gap:12, cursor:'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={data.declarationAgreed || false}
                    onChange={e => updateField('review', 'declarationAgreed', e.target.checked)}
                    style={{ marginTop:4, width:18, height:18, cursor:'pointer', accentColor:'#00d4aa' }}
                  />
                  <p style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.7, margin:0 }}>
                    I hereby declare that all information provided is true, accurate, and complete to the best of my knowledge. 
                    I understand that HCEN reserves the right to reject this application or terminate services if any information is found to be false or misleading. 
                    I authorize HCEN to verify submitted details with relevant authorities and to request compliance documents or a signed partnership agreement before or after listing.
                  </p>
                </label>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="apply-step-nav">
            <div className="apply-step-nav__secondary">
              {step > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={step === 1 || step === 0}
                  className="btn btn-secondary"
                  style={{ opacity: step <= 1 ? 0.5 : 1 }}
                >
                  <MdArrowBack /> Back
                </button>
              )}

              {step >= 1 && step < 4 && applicationId && (
                <button
                  type="button"
                  onClick={() => saveDraft(false)}
                  disabled={saving}
                  className="btn btn-secondary"
                  style={{
                    background:'rgba(245,158,11,0.1)',
                    border:'1px solid rgba(245,158,11,0.3)',
                    color:'#fbbf24',
                  }}
                >
                  {saving ? (
                    'Saving...'
                  ) : (
                    <>
                      <MdPause />
                      <span className="apply-save-label-full">Save &amp; Continue Later</span>
                      <span className="apply-save-label-short">Save draft</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="apply-step-nav__primary">
              {step === 0 ? null : step < 4 ? (
                <button type="button" onClick={nextStep} className="btn btn-primary" disabled={saving}>
                  Next <MdArrowForward />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !data.declarationAgreed}
                  className="btn btn-primary"
                  style={{ opacity: data.declarationAgreed ? 1 : 0.5 }}
                >
                  {submitting ? (
                    <><span className="anim-spin" style={{ width:16, height:16, border:'2px solid rgba(0,0,0,0.2)', borderTopColor:'var(--color-primary)', borderRadius:'50%', display:'inline-block' }} /> Submitting...</>
                  ) : (
                    <><MdCheckCircle /> Submit Application</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
